// Titles and descriptions for the site's fixed pages. Kept free of React so the
// build script (scripts/page-html.js) can also use it to write one HTML file per
// page, giving search engines and link previews the right text without running
// the app.
import { landings } from "../Pages/landingPages";
import { productJsonLd } from "./productData";

export const SITE = "https://technogeniousja.com";
export const BRAND = "Technogenious Solutions";
export const DEFAULT_DESCRIPTION =
  "Gaming machine parts and technical services in Kingston, Jamaica. Shop bill acceptors, printers, power supplies and more, or ask our team for help.";
// Link previews use a real installation photo (1400×788) rather than the small logo.
const DEFAULT_IMAGE = `${SITE}/installations/installation-updated-05.jpg`;

export type Meta = { title: string; description?: string; noindex?: boolean; image?: string };

// Search results show about 60 characters of a title, so the brand is added in
// full, shortened, or left off, whichever keeps the title within that.
const TITLE_MAX = 60;
export function brandTitle(title: string) {
  if (/technogenious/i.test(title)) return title;
  return [`${title} | ${BRAND}`, `${title} | Technogenious`].find((t) => t.length <= TITLE_MAX) || title;
}

export const pages: Record<string, Meta> = {
  "/": { title: "Technogenious | Gaming machine parts & service in Jamaica", description: DEFAULT_DESCRIPTION },
  "/products": { title: "Shop gaming machine parts & accessories", description: "Bill acceptors, ticket printers, touchscreens, power supplies and cables for gaming machines. Free pickup in Kingston, with local and international delivery." },
  "/gaming-machines": { title: "Gaming machines to buy, lease, or build", description: "Explore Technogenious gaming machines, see real installations across Jamaica, and request a quote to buy, lease, or build your own configuration." },
  "/build-your-machine": { title: "Build your gaming machine", description: "Choose a game, review the standard configuration, pick your hardware, and send your build to our team for a quotation." },
  "/leasing": { title: "Gaming machine leasing in Jamaica", description: "Lease gaming machines for your bar or venue on a 60/40 profit share, with no sign-up or installation fees. We install and maintain the machines." },
  "/services": { title: "Gaming machine repair & maintenance", description: "BGLC-licensed technical services in Jamaica: gaming machine repairs, installation, maintenance, manufacturing, and Pyramid bill acceptor support." },
  "/about": { title: "About Technogenious Solutions", description: "Technogenious Solutions Ltd. is a BGLC-licensed technical service provider in Kingston, Jamaica, supplying gaming machines, parts, and support since 2018." },
  "/contact": { title: "Contact us – 17 Molynes Road, Kingston", description: "Visit Technogenious Solutions at Shop 4B, 17 Molynes Road, Kingston 10, Monday to Saturday, 9:00 am to 6:00 pm, or call, email, or WhatsApp our team." },
  "/FAQs": { title: "Frequently asked questions", description: "Answers about ordering parts, delivery, prices in JMD, gaming machine leasing, warranties, and repairs." },
  "/warranties": { title: "Warranties & returns", description: "Warranty coverage for new and used gaming machines and repairs, plus how to make a claim, return goods, and get a refund." },
  "/termscondition": { title: "Terms & conditions", description: "Terms for using the Technogenious store, including prices, payment, pickup and delivery, cancellations, returns, and refunds." },
  "/privacy-policy": { title: "Privacy policy", description: "How Technogenious Solutions collects, uses, and protects your account, order, and enquiry information." },
  "/delete-account": { title: "Delete your account", description: "How to delete your Technogenious account on the website or in the app, and what happens to your information." },
  "/disclaimer": { title: "Disclaimer", description: "How to use the product information, technical guidance, and quotation tools on the Technogenious website." },
  "/request-quote": { title: "Request a quote", description: "Tell us what you need and our team will prepare a quotation for your parts, machine, or service." },
  ...Object.fromEntries(landings.map((l) => [l.path, { title: l.title, description: l.description, image: `${SITE}${l.image.src}` }])),
};

/** The final title, description, canonical URL, preview image and robots value for a page. */
export function resolveMeta(pathname: string, meta: Meta) {
  return {
    title: brandTitle(meta.title),
    description: (meta.description || DEFAULT_DESCRIPTION).replace(/\s+/g, " ").trim(),
    url: SITE + (pathname === "/" ? "/" : pathname.replace(/\/+$/, "")),
    image: /^https:\/\//.test(meta.image || "") ? meta.image! : DEFAULT_IMAGE,
    robots: meta.noindex ? "noindex, nofollow" : "index, follow",
  };
}

/** Plain-text summary for a product description that may contain HTML. */
export function summarise(text: unknown, max = 155) {
  if (typeof text !== "string") return "";
  const plain = text.replace(/<[^>]*>/g, " ").replace(/^\s*#{1,6}\s+.*$/gm, " ").replace(/^\s*[-*]\s+/gm, "").replace(/[*_`]+/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
  if (plain.length <= max) return plain;
  return plain.slice(0, max).replace(/\s+\S*$/, "") + "…";
}

// Search-friendly titles and descriptions for catalogue pages. A keyword suffix
// is added only when the title still fits in search results; the brand is then
// added if there is room.
const withSuffix = (base: string, suffix: string) =>
  brandTitle(`${base} – ${suffix}`).length <= TITLE_MAX ? `${base} – ${suffix}` : base;
export function productTitle(name: string) {
  return withSuffix(name.trim(), "Gaming parts, Jamaica");
}
export function machineTitle(name: string) {
  const n = name.trim();
  return withSuffix(/machine/i.test(n) ? n : `${n} gaming machine`, "Buy or lease in Jamaica");
}
/** Lower-cases ordinary words for use mid-sentence but keeps acronyms such as LCD. */
const midSentence = (text: string) => text.replace(/\b[A-Z][a-z]+\b/g, (w) => w.toLowerCase());
/** Title and description for a shop category page, e.g. "Power Supplies". */
export function categoryMeta(label: string): Meta {
  const name = label.trim() || "Products";
  return {
    title: withSuffix(name, "Gaming parts in Jamaica"),
    description: `Shop ${midSentence(name)} for gaming machines at Technogenious. Free pickup at 17 Molynes Road, Kingston, plus local and international delivery.`,
  };
}
/** Adds a local line when the description doesn't mention Jamaica and there is room. */
export function localDescription(text: string, tail: string, max = 160) {
  const base = text.trim();
  return !/jamaica|kingston/i.test(base) && base.length + 1 + tail.length <= max ? `${base} ${tail}` : base;
}

/** Title, description and preview image for a product page, from the product record. */
export function productMeta(item: { name: string; description?: unknown; image?: unknown; images?: unknown }): Meta {
  const description = localDescription(summarise(item.description) || `${item.name} from Technogenious Solutions in Kingston, Jamaica. Ask our team about compatibility, pickup, and delivery.`, "Free pickup in Kingston, Jamaica, or delivery.");
  const image = [item.image, ...(Array.isArray(item.images) ? item.images : [])].find((x: unknown) => typeof x === "string" && /^https:\/\//.test(x)) as string | undefined;
  return { title: productTitle(item.name), description, image };
}
/** Title, description and preview image for a gaming machine page. */
export function machineMeta(machine: { name: string; description?: unknown; mainImage?: unknown }): Meta {
  return {
    title: machineTitle(machine.name),
    description: localDescription(summarise(machine.description) || `${machine.name} gaming machine from Technogenious Solutions in Jamaica. Request a quote to buy, lease, or customise it.`, "Buy or lease in Jamaica."),
    image: typeof machine.mainImage === "string" && /^https:\/\//.test(machine.mainImage) ? machine.mainImage : undefined,
  };
}

/**
 * The same schema.org Product data the product page adds once it loads, from
 * the build's catalogue record, so search engines that read the HTML see the
 * price and stock too.
 */
export function productPageJsonLd(item: { id: string; name: string; description?: unknown; image?: unknown; images?: unknown; [key: string]: unknown }) {
  const image = [item.image, ...(Array.isArray(item.images) ? item.images : [])].filter((x: unknown): x is string => typeof x === "string" && /^https:\/\//.test(x));
  return productJsonLd({ ...item, _id: item.id }, productMeta(item).description || "", image);
}

const escape = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * The built index.html with one page's title, description, canonical URL and
 * preview tags, plus optional JSON-LD tagged with the same data-seo id the app
 * uses, so the app replaces it rather than adding a second copy.
 */
export function pageHtml(shell: string, pathname: string, meta: Meta, jsonLd?: { id: string; data: object }) {
  const m = resolveMeta(pathname, meta);
  const tags: [string, string, string][] = [
    ["name", "description", m.description], ["name", "robots", m.robots],
    ["property", "og:title", m.title], ["property", "og:description", m.description],
    ["property", "og:url", m.url], ["property", "og:image", m.image],
    ["name", "twitter:title", m.title], ["name", "twitter:description", m.description],
  ];
  let html = shell.replace(/<title>[\s\S]*?<\/title>/, `<title>${escape(m.title)}</title>`);
  const added: string[] = [];
  for (const [attr, key, value] of tags) {
    const tag = `<meta ${attr}="${key}" content="${escape(value)}"/>`;
    const existing = new RegExp(`<meta\\s+${attr}="${key.replace(/[.:]/g, "\\$&")}"[^>]*>`);
    if (existing.test(html)) html = html.replace(existing, () => tag);
    else added.push(tag);
  }
  added.push(`<link rel="canonical" href="${escape(m.url)}"/>`);
  if (jsonLd) added.push(`<script type="application/ld+json" data-seo="${escape(jsonLd.id)}">${JSON.stringify(jsonLd.data).replace(/</g, "\\u003c")}</script>`);
  return html.replace("</head>", () => added.join("") + "</head>");
}
